import type { AudienceInput, GroupSummary, LiveAudience } from '@ralph/contracts'
import { approvalNeeded, splitChange } from '@ralph/contracts'

/**
 * The picker's decisions, as pure functions (PRD-26 D-30).
 *
 * Kept out of the component because each is a rule worth a test: which section a group sits in
 * before anybody types, which marks an option carries, and the two sentences under the picker —
 * how the reach changes, and whether an administrator will review it. The review sentence is
 * computed with the same `approvalNeeded` / `splitChange` the server decides with, so what the
 * picker predicts is what happens; the server's answer still counts (a manager removed a moment
 * ago is judged by the server as they stand then).
 */

export type PickerViewer = {
  isAdmin: boolean
  groupIds: readonly string[]
  managedGroupIds: readonly string[]
}

export type SectionKey = 'manage' | 'member' | 'other'

export type PickerSection = { key: SectionKey; groups: GroupSummary[] }

/**
 * Groups before anybody types: the ones you manage, then the ones you are in, then the rest —
 * the order in which people usually mean to share. Empty sections are dropped. Within a section,
 * the server's order (by name) is kept, so rows do not jump as boxes are ticked.
 */
export const pickerSections = (groups: readonly GroupSummary[]): PickerSection[] => {
  const manage = groups.filter((group) => group.isManager)
  const member = groups.filter((group) => group.isMember && !group.isManager)
  const other = groups.filter((group) => !group.isMember && !group.isManager)
  const sections: PickerSection[] = [
    { key: 'manage', groups: manage },
    { key: 'member', groups: member },
    { key: 'other', groups: other }
  ]
  return sections.filter((section) => section.groups.length > 0)
}

export type OptionMark = 'shared_now' | 'needs_review'

/**
 * What an option says beside its name: "shared now" when people in it use the item now, and
 * "needs review" when adding it would wait for an administrator (never for an administrator; never
 * for a group already in the stored audience or one the viewer manages, D-13). Systems pass
 * `reviewed: false`.
 *
 * `live` is the stored audience the review rule compares against — for an agent that is not live
 * too (D-57). `inForce` is who uses it now, when that differs: nobody, for a draft or a retired or
 * paused agent, whose stored groups are not "shared now". It defaults to `live`.
 */
export const optionMarks = (
  group: Pick<GroupSummary, 'id' | 'isManager'>,
  input: { live: LiveAudience | null; inForce?: LiveAudience | null; viewer: PickerViewer; reviewed: boolean }
): OptionMark[] => {
  const marks: OptionMark[] = []
  const liveGroups = input.live?.kind === 'groups' ? input.live.groupIds : []
  const isLive = input.live?.kind === 'everyone' || liveGroups.includes(group.id)
  const inForce = input.inForce === undefined ? input.live : input.inForce
  if (inForce?.kind === 'groups' && inForce.groupIds.includes(group.id)) marks.push('shared_now')
  if (input.reviewed && !input.viewer.isAdmin && !isLive && !group.isManager) marks.push('needs_review')
  return marks
}

/** The audience a request would carry, normalised the way the server reads it. */
export const liveOf = (audience: AudienceInput): LiveAudience => ({
  kind: audience.kind,
  groupIds: audience.kind === 'groups' ? [...new Set(audience.groupIds)] : []
})

/** Nothing to save: the same kind, and the same groups when that matters. */
export const isUnchanged = (live: LiveAudience | null, next: AudienceInput): boolean => {
  if (live === null) return false
  if (live.kind !== next.kind) return false
  if (next.kind !== 'groups') return true
  const a = new Set(live.groupIds)
  const b = new Set(next.groupIds)
  return a.size === b.size && [...a].every((id) => b.has(id))
}

export type ReviewLine =
  | { kind: 'none' }
  | { kind: 'administrator' }
  | { kind: 'live_now'; managedAdded: string[] }
  | { kind: 'everyone' }
  | { kind: 'groups'; waiting: string[]; nowAdded: string[]; nowRemoved: string[] }

/**
 * Whether the change waits for an administrator, and what goes live now (D-13, D-40).
 *
 * `names` resolves ids to names for every group the sentence might mention — chosen, live or
 * managed. Unknown ids read as nothing rather than as an id (GRP-102).
 */
export const reviewLine = (input: {
  viewer: PickerViewer
  live: LiveAudience | null
  requested: AudienceInput
  names: ReadonlyMap<string, string>
}): ReviewLine => {
  const requested = liveOf(input.requested)
  const live: LiveAudience = input.live ?? { kind: 'private', groupIds: [] }
  if (input.live !== null && isUnchanged(input.live, input.requested)) return { kind: 'none' }
  if (input.viewer.isAdmin) return { kind: 'administrator' }

  const nameOf = (id: string) => input.names.get(id)
  const known = (ids: readonly string[]) => ids.map(nameOf).filter((name): name is string => Boolean(name))

  const verdict = approvalNeeded({
    actorIsAdmin: false,
    actorManagedGroupIds: input.viewer.managedGroupIds,
    live,
    requested
  })
  const liveGroups = live.kind === 'groups' ? live.groupIds : []
  const added = requested.kind === 'groups' ? requested.groupIds.filter((id) => !liveGroups.includes(id)) : []

  if (!verdict.required) {
    return {
      kind: 'live_now',
      managedAdded: known(
        live.kind === 'everyone' ? [] : added.filter((id) => input.viewer.managedGroupIds.includes(id))
      )
    }
  }
  if (verdict.reason === 'everyone') return { kind: 'everyone' }

  const split = splitChange({
    actorIsAdmin: false,
    actorManagedGroupIds: input.viewer.managedGroupIds,
    live,
    requested
  })
  const now = split.now
  const nowGroups = now?.kind === 'groups' ? now.groupIds : []
  return {
    kind: 'groups',
    waiting: known(verdict.unmanagedGroupIds),
    nowAdded: known(nowGroups.filter((id) => !liveGroups.includes(id))),
    nowRemoved: known(now === null ? [] : liveGroups.filter((id) => !nowGroups.includes(id)))
  }
}

export type ReachLine =
  | { kind: 'pending' }
  | { kind: 'no_groups' }
  | { kind: 'private'; people: number | null }
  | { kind: 'everyone'; people: number | null }
  | { kind: 'groups'; people: number | null; delta: number | null }

/**
 * Whether the count on screen is still the previous choice's. The reach query keeps the last
 * answer while a new one loads (`keepPreviousData`), and reports "not loading" while it does: read
 * as final, a sentence stored as what somebody agreed to would carry the wrong audience's count.
 */
export const reachPending = (query: { isLoading: boolean; isPlaceholderData: boolean }): boolean =>
  query.isLoading || query.isPlaceholderData

/** How many people the choice reaches, and how that compares with now (GRP-011). */
export const reachLine = (input: {
  requested: AudienceInput
  people: number | null
  current: number | null
}): ReachLine => {
  if (input.requested.kind === 'groups' && input.requested.groupIds.length === 0) return { kind: 'no_groups' }
  if (input.requested.kind === 'private') return { kind: 'private', people: input.people }
  if (input.requested.kind === 'everyone') return { kind: 'everyone', people: input.people }
  return {
    kind: 'groups',
    people: input.people,
    delta: input.people === null || input.current === null ? null : input.people - input.current
  }
}
